import React, { useEffect, useRef, useState } from 'react';
import { webLoadingFalse, webNavigate } from '../../services/navigate.service';
import CloseIcon from '../../images/CloseIcon';
import { deleteDevices, getListDevices } from '../../services/devices.service';
import PlusIconWhite from '../../images/PlusIconWhite';
import EditIconV2 from '../../images/EditIconV2';
import DeleteIconV2 from '../../images/DeleteIconV2';
import { useResponsive } from '../../hook/useResponsive';
import DeviceIcon from '../../images/DeviceIcon';
import { showDeleteToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import {
  ListColumn,
  ListQuery,
  ListRowAction,
  ListScreen,
  ListScreenHandle,
  Pill,
  cells,
  t,
} from '../../components/list';

interface DeviceRow {
  id: string;
  serialNo: string;
  deviceName: string;
  location: { name: string } | null;
  updatedAt: Date;
  status: { statusName: string } | null;
}
interface DevicesResponse {
  data: {
    data: {
      devices: DeviceRow[];
      count: number;
    };
  };
}

/** The device statuses the old TagStatus rendered green; anything else is grey. */
const isDeviceStatusOn = (statusName: string) => statusName === 'Active';

const Devices = () => {
  const preLanguage = localStorage.getItem('language');
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<ListScreenHandle>(null);
  const [language, setLanguage] = useState<any>(null);
  const [showDelete, setShowDelete] = useState<boolean>(false);
  const [idDelete, setIdDelete] = useState<string>('');
  const { isMobile } = useResponsive();
  const inforTable: any = {
    img: <DeviceIcon />,
    title:
      language && language?.['No devices yet']
        ? language?.['No devices yet']
        : 'No devices yet',
    path: '/devices/',
    desc:
      language && language?.['Assign and manage which users can access each POS device to ensure secure operations and maintain accountability for all transactions.']
        ? language?.['Assign and manage which users can access each POS device to ensure secure operations and maintain accountability for all transactions.']
        : 'Assign and manage which users can access each POS device to ensure secure operations and maintain accountability for all transactions.',
  };
  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
      event.preventDefault();
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  /** One page of devices; the search box maps onto the endpoint's `searchValue`. */
  const fetchDevices = async (query: ListQuery) => {
    webLoadingFalse();
    const response: DevicesResponse = await getListDevices(query.page, query.limit, query.search);
    return {
      rows: response.data.data.devices ?? [],
      count: response.data.data.count ?? 0,
    };
  };

  const goToDevice = (id: string) => webNavigate('/' + preLanguage + '/devices/' + id);

  const handleShowDelete = (id: string) => {
    setShowDelete(true);
    setIdDelete(id);
  };
  const handleDeleteEmployee = async () => {
    try {
      const response = await deleteDevices(idDelete);
      showDeleteToast(language?.['Device Deleted !'] ?? 'Device Deleted !');
    } catch (error) {
      console.error('Error loading terms:', error);
      showApiError(error, { language });
    } finally {
      listRef.current?.reload();
      setShowDelete(false);
    }
  };

  /* ---------------- List kit configuration ---------------- */

  const columns: ListColumn<DeviceRow>[] = [
    {
      key: 'deviceName',
      label: 'Device Name',
      width: 240,
      hideable: false,
      render: cells.primary(d => d.deviceName),
      mobile: 'title',
      onClick: d => goToDevice(String(d.id)),
    },
    { key: 'serialNo', label: 'Serial No', width: 180, mobile: 'subtitle' },
    {
      key: 'location',
      label: 'Location',
      width: 200,
      value: d => d.location?.name ?? '',
      mobile: 'subtitle',
    },
    {
      key: 'updatedAt',
      label: 'Last Update',
      width: 130,
      render: cells.date(d => d.updatedAt),
      mobile: 'sheet',
    },
    {
      key: 'status',
      label: 'Status',
      width: 100,
      value: (d, ctx) => {
        const name = d.status?.statusName ?? '';
        return name ? t(ctx.language, name, name) : '';
      },
      // The status table's name (Active / Inactive / ...), green only when Active.
      render: (d, ctx) => {
        const name = d.status?.statusName ?? '';
        return name ? <Pill label={t(ctx.language, name, name)} kind={isDeviceStatusOn(name) ? 'ok' : 'off'} /> : null;
      },
      mobile: 'trailing',
    },
  ];

  const rowActions: ListRowAction<DeviceRow>[] = [
    {
      key: 'edit',
      label: 'Edit',
      icon: <EditIconV2 />,
      sheetIcon: 'inventory',
      onRun: d => goToDevice(String(d.id)),
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteIconV2 />,
      sheetIcon: 'close',
      danger: true,
      onRun: d => handleShowDelete(String(d.id)),
    },
  ];

  return (
    <>
      <div
        className="h-full relative"
        style={{
          overflowY: isMobile ? 'auto' : undefined,
          paddingBottom: isMobile ? '24px' : undefined,
          display: isMobile ? undefined : 'flex',
          flexDirection: isMobile ? undefined : 'column',
        }}
      >
        <div className="lg:flex items-center justify-between">
          <p
            style={{ color: '#222525' }}
            className="text-[24px] leading-[130%] font-medium font-inter"
          >
            {language && language?.['Devices']
              ? language?.['Devices']
              : 'Devices'}
          </p>
          <div
            className="cursor-pointer flex items-center justify-center"
            style={{
              gap: '8px',
              padding: "8px 24px 8px 16px",
              backgroundColor: '#0F7679',
              borderRadius: '30px',
              width: 'fit-content',
              marginTop: isMobile ? '20px' : '0px',
            }}
            onClick={() => {
              webNavigate('/' + preLanguage + '/devices/new');
            }}
          >
            <div style={{ width: '24px', height: '24px' }} className="flex items-center justify-center">
              <PlusIconWhite />
            </div>
            <p
              style={{ color: '#FFFFFF' }}
              className="text-[14px] leading-[130%] font-medium font-inter"
            >
              {language && language?.['Add device']
                ? language?.['Add device']
                : 'Add device'}
            </p>
          </div>
        </div>
        <ListScreen
          ref={listRef}
          language={language}
          columns={columns}
          fetchRows={fetchDevices}
          searchPlaceholder="Search devices"
          searchInputRef={searchInputRef}
          rowActions={rowActions}
          countNoun="devices"
          emptyState={inforTable}
        />
      </div>
      <div
        className={`fixed inset-0 flex items-center justify-center px-4 ${showDelete ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          } transition-opacity duration-300 ease-in-out`}
        style={{ backgroundColor: "#22252552", zIndex: "200", transition: "opacity 0.3s ease, visibility 0.3s ease", width: "100%" }}
      >
        <div
          className={`transform transition-transform duration-300 ease-in-out ${showDelete ? "translate-y-0" : "translate-y-4 opacity-0"}`} style={{
            padding: "24px", borderRadius: "24px", backgroundColor: "#FFFFFF", width: "530px", maxWidth: "calc(100vw - 32px)", transition: "transform 0.3s ease, opacity 0.3s ease",
          }}
        >
          <div
            className="flex items-center justify-between"
          >
            <p style={{ fontSize: '20px' }} className="font-semibold font-inter text-main leading-[24px]">
              {language && language?.['Delete device?']
                ? language?.['Delete device?']
                : 'Delete device?'}
            </p>
            <div
              onClick={() => setShowDelete(false)}
              className="flex items-center justify-center cursor-pointer"
              style={{
                width: '24px',
                height: '24px',
              }}
            >
              <CloseIcon />
            </div>
          </div>
          <div className="flex items-center justify-between" style={{ padding: '20px 16px' }}>
            <p className="text-[14px] font-inter leading-[24px]" style={{ color: '#6A7878' }}>
              {language && language?.["This device will be permanently deleted. Are you sure you want to continue?"] ? language?.["This device will be permanently deleted. Are you sure you want to continue?"] : "This device will be permanently deleted. Are you sure you want to continue?"}
            </p>
          </div>
          <div className="flex items-center justify-end" style={{ marginTop: '24px', gap: '12px' }}>
            <div
              onClick={() => setShowDelete(false)}
              className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
              style={{
                borderRadius: '30px',
                padding: '10px 24px',
                border: '1px solid #C9D7D8',
                color: '#222525',
              }}
            >
              {language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'}
            </div>
            <div
              onClick={handleDeleteEmployee}
              className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
              style={{
                borderRadius: '30px',
                padding: '10px 24px',
                backgroundColor: '#BA0E13',
                color: '#FFFFFF',
              }}
            >
              {language && language?.['Delete'] ? language?.['Delete'] : 'Delete'}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Devices;
